/**
 * Note Sections List
 *
 * Container component that renders all note sections with:
 * - Loading states
 * - Empty states
 * - Section highlighting for AI changes
 * - Streaming updates
 * - Compliance rule indicators from JotCoach encounter session
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { NoteSection } from '@features/notes';
import { Box, Typography } from '@mui/material';

import type { AnyBlockNoteEditor } from '../../../utils/blocknote/types';
import type { UseApprovalNavigationReturn } from '../hooks/useApprovalNavigation';
import type { EditPreview } from '../types';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';
import { NoteSectionItem } from './NoteSectionItem';

// ============================================================================
// JOTCOACH ENCOUNTER SESSION TYPES
// ============================================================================
// Type definitions for JotCoach encounter session data
// Used for compliance rule indicators on note sections
//
// IMPORTANT: There are TWO data patterns for compliance rule citations:
//
// 1. PREFERRED PATTERN: `note_section_rule_citations`
//    A direct map of section names to rule IDs at the session level.
//    Example: { "Assessment": ["rule-id-1", "rule-id-2"], "HPI": ["rule-id-3"] }
//
// 2. DEPRECATED PATTERN: `documentation_analysis.flags[].rule_citations`
//    Rule IDs are nested inside flags, grouped by `note_section`.
//    This pattern should be removed once the backend fully migrates to pattern 1.
//    Example: flags[{ note_section: "MSE", rule_citations: [{ rule_id: "..." }] }]
//
// The code below checks for pattern 1 first, then falls back to pattern 2.
// ============================================================================

interface RuleCitation {
  rule_id: string;
  rationale?: string;
}

interface DocumentationFlag {
  note_section: string;
  rule_citations?: RuleCitation[];
  // Other flag fields omitted for brevity
}

interface DocumentationAnalysis {
  flags?: DocumentationFlag[];
  // Other analysis fields omitted for brevity
}

interface AttestationEdit {
  note_section: string;
  rationale: string;
  rule: string;
  text: string;
  title: string;
}

interface JotCoachEncounterSession {
  sessions?: {
    [key: string]: {
      // PREFERRED: Direct section -> rule IDs mapping
      note_section_rule_citations?: {
        [sectionName: string]: string[];
      };
      // Attestation edits surfaced by JotCoach
      attestation_edits?: AttestationEdit[];
      documentation_analysis?: DocumentationAnalysis & {
        attestation_edits?: AttestationEdit[];
      };
      rules_analysis?: {
        attestation_edits?: AttestationEdit[];
      };
      // DEPRECATED: Rule IDs nested in documentation_analysis.flags
      // TODO: Remove this fallback once backend fully migrates to note_section_rule_citations
    };
  };
}

/**
 * Normalize a section name for matching against note_section_rule_citations keys
 * Converts to lowercase, replaces non-alphanumeric with dashes, removes leading/trailing dashes
 */
function normalizeSectionName(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Check if two normalized section names match, using flexible matching strategies:
 * 1. Exact match
 * 2. One is a prefix of the other (e.g., "assessment" matches "assessment-mdm")
 * 3. Common aliases (e.g., "medications" matches "medication-history-customized")
 */
function sectionsMatch(normalizedSectionName: string, normalizedFlagSection: string): boolean {
  // Exact match
  if (normalizedSectionName === normalizedFlagSection) {
    return true;
  }

  // One is a prefix of the other (handles "Assessment" matching "Assessment/MDM" -> "assessment-mdm")
  // Check if one starts with the other followed by a dash or is exactly equal
  if (
    normalizedFlagSection.startsWith(normalizedSectionName + '-') ||
    normalizedSectionName.startsWith(normalizedFlagSection + '-')
  ) {
    return true;
  }

  // Common aliases/mappings
  const aliasMap: Record<string, string[]> = {
    assessment: ['assessment-mdm', 'assessment-and-plan'],
    medications: ['medication-history-customized', 'medication-history', 'medications-list'],
    'medication-history-customized': ['medications', 'medication-history', 'medications-list'],
    'mental-status-exam': ['mse'],
    mse: ['mental-status-exam'],
    'history-of-present-illness': ['hpi'],
    hpi: ['history-of-present-illness'],
  };

  const aliases = aliasMap[normalizedSectionName] || [];
  if (aliases.includes(normalizedFlagSection)) {
    return true;
  }

  const flagAliases = aliasMap[normalizedFlagSection] || [];
  if (flagAliases.includes(normalizedSectionName)) {
    return true;
  }

  return false;
}

/**
 * DEPRECATED: Extract rule IDs from documentation_analysis.flags grouped by section name
 * This is a fallback for the older JotCoach data format.
 * TODO: Remove once backend fully migrates to note_section_rule_citations
 */
function extractRuleIdsFromFlags(flags: DocumentationFlag[]): Map<string, string[]> {
  const map = new Map<string, string[]>();

  flags.forEach(flag => {
    if (!flag.note_section || !flag.rule_citations || flag.rule_citations.length === 0) {
      return;
    }

    const normalizedSection = normalizeSectionName(flag.note_section);
    const ruleIds = flag.rule_citations.map(citation => citation.rule_id);

    // Merge with existing rule IDs for this section (multiple flags may reference same section)
    const existing = map.get(normalizedSection) || [];
    const merged = [...new Set([...existing, ...ruleIds])]; // Deduplicate
    map.set(normalizedSection, merged);
  });

  return map;
}

interface NoteSectionsListProps {
  /**
   * Array of note sections
   */
  sections: NoteSection[];

  /**
   * IDs of sections affected by pending approval
   */
  affectedSectionIds?: string[];

  /**
   * Map of section IDs to pending AI-generated content
   */
  sectionUpdates?: Record<string, string>;

  /**
   * Array of section IDs that are currently being streamed
   */
  streamingSections?: string[];

  /**
   * Array of edit previews with diff data (for affected sections)
   * Contains original_text, edited_text, and diff operations
   */
  edits?: EditPreview[];

  /**
   * Callback when section content changes
   * Returns a Promise that resolves when the save completes
   */
  onSectionChange: (index: number, editedText: string) => Promise<void>;

  /**
   * Encounter ID for analytics metadata
   */
  encounterId?: string;

  /**
   * Whether sections should be editable
   */
  editable?: boolean;

  /**
   * Callback when user approves the AI suggestion (dismisses affected state)
   */
  onApprove?: (sectionId: string) => void;

  /**
   * Callback when user reverts the AI suggestion
   */
  onRevert?: (sectionId: string) => void;

  /**
   * Map of section IDs to their previous content (before AI edit)
   */
  previousContent?: Record<string, string>;

  /**
   * Navigation functions and state from useApprovalNavigation hook
   */
  navigation?: UseApprovalNavigationReturn;

  /**
   * JotCoach encounter session data for compliance rule indicators
   * Contains note_section_rule_citations mapped by section name
   */
  jotcoachEncounterSession?: JotCoachEncounterSession | null;

  /**
   * Note ID for looking up the correct session in jotcoachEncounterSession
   */
  noteId?: string;

  /**
   * Callback when a save operation starts (for navigation guard)
   * Receives the section ID and save promise to track completion
   */
  onSaveStart?: (sectionId: string, savePromise: Promise<void>) => void;

  /**
   * Callback when section content is modified (marks as dirty)
   */
  onMarkDirty?: (sectionId: string) => void;

  /**
   * Map of section_id to audit issue count (warnings + non-compliant)
   * Computed from RTK Query audit data and passed down
   */
  sectionIssueMap?: Record<string, number>;

  /**
   * Streaming sections from note generation v2 workflow
   * Used to display sections as they are generated in real-time
   */
  generationStreamingSections?: Array<{
    id: string;
    title: string;
    text: string;
  }>;

  /**
   * Whether note generation is in progress (v2 streaming flow)
   * When true, displays streaming sections with loading indicators
   */
  isGenerating?: boolean;

  /**
   * Callback when BlockNote editor registry is updated
   * Allows parent to access editor instances for copy operations
   */
  onEditorsReady?: (editorsMap: Map<string, AnyBlockNoteEditor>) => void;
}

export const NoteSectionsList: React.FC<NoteSectionsListProps> = ({
  sections,
  affectedSectionIds = [],
  sectionUpdates = {},
  streamingSections = [],
  edits,
  onSectionChange,
  encounterId,
  editable = true,
  onApprove,
  onRevert,
  previousContent = {},
  navigation,
  jotcoachEncounterSession,
  noteId,
  onSaveStart,
  onMarkDirty,
  sectionIssueMap = {},
  generationStreamingSections,
  isGenerating = false,
  onEditorsReady,
}) => {
  // Memoize affected section IDs as a Set for O(1) lookup
  const affectedSet = useMemo(() => new Set(affectedSectionIds), [affectedSectionIds]);

  // Memoize streaming section IDs as a Set for O(1) lookup
  const streamingSet = useMemo(() => new Set(streamingSections), [streamingSections]);

  // ============================================================================
  // BLOCKNOTE EDITOR REGISTRY
  // ============================================================================
  // Store BlockNote editor instances for copy operations (follows JotDocs pattern)
  const blockNoteEditorsRef = useRef<Map<string, AnyBlockNoteEditor>>(new Map());

  // Callback to register/unregister editor instances from NoteSectionItem components
  const handleEditorReady = useCallback(
    (sectionId: string, editor: AnyBlockNoteEditor | null) => {
      if (editor) {
        blockNoteEditorsRef.current.set(sectionId, editor);
      } else {
        blockNoteEditorsRef.current.delete(sectionId);
      }
      // Notify parent of updated editor registry
      if (onEditorsReady) {
        onEditorsReady(new Map(blockNoteEditorsRef.current));
      }
    },
    [onEditorsReady],
  );

  // Cleanup: Clear editor registry on unmount
  useEffect(() => {
    return () => {
      blockNoteEditorsRef.current.clear();
    };
  }, []);

  // ============================================================================
  // COMPLIANCE RULE DATA EXTRACTION
  // ============================================================================
  // Extract compliance rule IDs per section from JotCoach encounter session
  // Pattern borrowed from: src/web-app/src/features/jotdocs/components/jot-doc-note-section-view.tsx

  /**
   * Find matching session for this note in encounter session
   * Uses note:{noteId} as the session key
   */
  const matchingSession = useMemo(() => {
    if (!noteId || !jotcoachEncounterSession?.sessions) {
      return null;
    }
    const noteSessionKey = `note:${noteId}`;
    return jotcoachEncounterSession.sessions[noteSessionKey] || null;
  }, [noteId, jotcoachEncounterSession]);

  /**
   * Map section IDs to their matching compliance rule IDs
   *
   * Checks TWO data sources (in order of preference):
   * 1. PREFERRED: note_section_rule_citations - direct section -> rule IDs mapping
   * 2. DEPRECATED: documentation_analysis.flags - rule IDs nested in flags by note_section
   *    TODO: Remove fallback to flags once backend fully migrates to note_section_rule_citations
   *
   * Normalizes section names for matching (lowercase, dashes for non-alphanumeric)
   */
  const sectionComplianceRuleIdsMap = useMemo(() => {
    const map = new Map<string, string[]>();

    if (!matchingSession || sections.length === 0) {
      return map;
    }

    // -------------------------------------------------------------------------
    // PATTERN 1 (PREFERRED): Use note_section_rule_citations if available
    // -------------------------------------------------------------------------
    const ruleCitations = matchingSession.note_section_rule_citations;
    if (ruleCitations && Object.keys(ruleCitations).length > 0) {
      // Match sections to rule IDs by normalizing section names
      sections.forEach(section => {
        const normalizedSectionName = normalizeSectionName(section.name);

        // Check if any key in ruleCitations matches this section's normalized name (flexible matching)
        const matchingRuleIds = Object.entries(ruleCitations)
          .filter(([sectionName]) => {
            const normalizedKey = normalizeSectionName(sectionName);
            return sectionsMatch(normalizedSectionName, normalizedKey);
          })
          .flatMap(([, ruleIds]) => ruleIds);

        if (matchingRuleIds.length > 0) {
          map.set(section.section_id, matchingRuleIds);
        }
      });

      return map;
    }

    // -------------------------------------------------------------------------
    // PATTERN 2 (DEPRECATED): Fall back to documentation_analysis.flags
    // TODO: Remove this fallback once backend fully migrates to note_section_rule_citations
    // -------------------------------------------------------------------------
    const flags = matchingSession.documentation_analysis?.flags;
    if (flags && flags.length > 0) {
      // Extract rule IDs from flags grouped by normalized section name
      const flagsRuleMap = extractRuleIdsFromFlags(flags);

      // Match sections to rule IDs by normalizing section names (using flexible matching)
      sections.forEach(section => {
        const normalizedSectionName = normalizeSectionName(section.name);

        // Try exact match first
        let ruleIds = flagsRuleMap.get(normalizedSectionName);

        // If no exact match, try flexible matching
        if (!ruleIds || ruleIds.length === 0) {
          for (const [flagSectionKey, flagRuleIds] of flagsRuleMap.entries()) {
            if (sectionsMatch(normalizedSectionName, flagSectionKey)) {
              ruleIds = flagRuleIds;
              break;
            }
          }
        }

        if (ruleIds && ruleIds.length > 0) {
          map.set(section.section_id, ruleIds);
        }
      });
    }

    return map;
  }, [matchingSession, sections]);

  /**
   * Map section IDs to their matching attestation edits
   *
   * Checks THREE data sources (in order of preference):
   * 1. attestation_edits at the session level
   * 2. documentation_analysis.attestation_edits
   * 3. rules_analysis.attestation_edits (legacy)
   */
  const sectionAttestationEditsMap = useMemo(() => {
    const map = new Map<string, AttestationEdit[]>();

    if (!matchingSession || sections.length === 0) {
      return map;
    }

    const attestationSources: AttestationEdit[] =
      matchingSession.attestation_edits ||
      matchingSession.documentation_analysis?.attestation_edits ||
      matchingSession.rules_analysis?.attestation_edits ||
      [];

    if (attestationSources.length === 0) {
      return map;
    }

    // Group edits by normalized note_section
    const editsBySlug = new Map<string, AttestationEdit[]>();
    attestationSources.forEach(edit => {
      if (!edit.note_section) return;
      const slug = normalizeSectionName(edit.note_section);
      const existing = editsBySlug.get(slug) || [];
      editsBySlug.set(slug, [...existing, edit]);
    });

    // Match note sections to attestation edits
    sections.forEach(section => {
      const sectionSlug = normalizeSectionName(section.name);
      const edits = editsBySlug.get(sectionSlug);
      if (edits && edits.length > 0) {
        map.set(section.section_id, edits);
      }
    });

    return map;
  }, [matchingSession, sections]);

  // ============================================================================
  // END COMPLIANCE RULE DATA EXTRACTION
  // ============================================================================

  // Create a set of generation streaming section IDs for O(1) lookup
  const generationStreamingSet = useMemo(
    () => new Set(generationStreamingSections?.map(s => s.id) || []),
    [generationStreamingSections],
  );

  // Determine which sections to display:
  // - If generating AND we have streaming data OR if we have no real sections yet but have streaming data
  // - Otherwise, show regular sections
  // NOTE: We need to check for actual streaming data, not just isGenerating flag,
  // because isGenerating might be true before SSE has returned any sections
  const hasRealSections = sections && sections.length > 0;
  const hasStreamingData = generationStreamingSections && generationStreamingSections.length > 0;
  const hasGenerationSections = (isGenerating && hasStreamingData) || (!hasRealSections && hasStreamingData);

  // ============================================================================
  // MERGE SECTIONS FOR RENDERING
  // ============================================================================
  // When generating, merge streaming sections with loaded sections
  // Priority: loaded sections take precedence, streaming sections fill gaps
  // NOTE: This useMemo must be called BEFORE any early returns to satisfy React's rules of hooks
  const displaySections: Array<
    NoteSection | { section_id: string; name: string; text: string; isGenerationStreaming: true }
  > = useMemo(() => {
    // If not generating or no streaming sections, return regular sections
    if (!hasGenerationSections || !generationStreamingSections) {
      return sections;
    }

    // Create a map of loaded sections by ID
    const loadedSectionsMap = new Map(sections.map(s => [s.section_id, s]));

    // Build display list: prefer loaded sections, fall back to streaming
    const result: Array<
      NoteSection | { section_id: string; name: string; text: string; isGenerationStreaming: true }
    > = [];
    const addedIds = new Set<string>();

    // Add streaming sections (they come in order from the stream)
    for (const streamingSection of generationStreamingSections) {
      const loadedSection = loadedSectionsMap.get(streamingSection.id);
      if (loadedSection) {
        // Use loaded section if available
        result.push(loadedSection);
      } else {
        // Use streaming section
        result.push({
          section_id: streamingSection.id,
          name: streamingSection.title,
          text: streamingSection.text,
          isGenerationStreaming: true,
        });
      }
      addedIds.add(streamingSection.id);
    }

    // Add any loaded sections not in streaming (in case stream is incomplete)
    for (const section of sections) {
      if (!addedIds.has(section.section_id)) {
        result.push(section);
      }
    }

    return result;
  }, [sections, hasGenerationSections, generationStreamingSections]);

  // Empty state - show only if no sections AND not generating with streaming sections
  if ((!sections || sections.length === 0) && !hasGenerationSections) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {isGenerating ? 'Generating Note...' : 'No Sections Found'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isGenerating
            ? 'Sections will appear here as they are generated.'
            : "This note doesn't have any sections yet. Start a chat to generate content."}
        </Typography>
      </Box>
    );
  }

  return (
    <NoteSectionContainer
      sx={{
        py: 6,
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
      }}
    >
      {displaySections.map((section, index) => {
        const sectionId = section.section_id;
        const isGenerationStreaming =
          'isGenerationStreaming' in section && section.isGenerationStreaming;
        const isAffected = affectedSet.has(sectionId);
        const pendingContent = sectionUpdates[sectionId];
        // Only mark as streaming during active AI writing or generation streaming
        const isActivelyStreaming =
          streamingSet.has(sectionId) || generationStreamingSet.has(sectionId);
        const isStreaming = isActivelyStreaming || isGenerationStreaming;

        // Get compliance rule IDs for this section (if any)
        const complianceRuleIds = sectionComplianceRuleIdsMap.get(sectionId);
        const attestationEdits = sectionAttestationEditsMap.get(sectionId);

        // For generation streaming sections, create a compatible NoteSection object
        // NoteSection only requires: section_id, name, text
        const displaySection: NoteSection = isGenerationStreaming
          ? {
              section_id: section.section_id,
              name: section.name,
              text: section.text,
            }
          : (section as NoteSection);

        return (
          <NoteSectionItem
            key={sectionId}
            section={displaySection}
            index={index}
            isAffected={isAffected}
            isStreaming={isStreaming}
            pendingContent={pendingContent}
            edits={edits}
            onChange={onSectionChange}
            encounterId={encounterId}
            editable={editable && !isGenerationStreaming}
            onApprove={onApprove ? () => onApprove(sectionId) : undefined}
            onRevert={onRevert ? () => onRevert(sectionId) : undefined}
            previousContent={previousContent[sectionId]}
            complianceRuleIds={complianceRuleIds}
            attestationEdits={attestationEdits}
            onSaveStart={onSaveStart}
            onMarkDirty={onMarkDirty}
            sectionIssueCount={sectionIssueMap[sectionId] || 0}
            onEditorReady={editor => handleEditorReady(sectionId, editor)}
          />
        );
      })}
    </NoteSectionContainer>
  );
};
